import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import PubSub from 'pubsub-js';

import {
  Sticky,
  StickyContainer,
} from 'react-sticky';

import withTheme from 'core/hocs/withTheme';
import { sizeIndex } from 'core/components/Shapka';

import styles from './index.styl';

// Состояния
const INITIAL = 'initial';
const UNSTICKY = 'unsticky';
const STICKY_BOTTOM = 'sticky_bottom';
const FINISH = 'finish';
const STATIC_OFFSET = 'static_offset';
const STATIC_FINISH = 'static_finish';

/**
 * Компонент для прикрепления содержимого к верху страницы во время скрола.
 *
 * Важно помнить, что для правильной работы этого компонента он должен
 * располагаться во флекс контейнере чтоб занимать все свободное пространство.
 */
class StickyPortal extends PureComponent {
  constructor(props) {
    super(props);
    this.stickyRef = React.createRef();
    this.state = {
      static_offset: false,
      static_finish: false,
    };
  }

  componentDidMount() {
    if (this.props.listenStickyRemoveEvent) {
      const stickyEl = this.stickyRef.current;
      const adEl = stickyEl.querySelector('[data-capirs]');
      if (!adEl) return;

      const adDomId = JSON.parse(adEl.dataset.capirs).domId;

      this.stickyAdToken = PubSub.subscribe(`sticky.remove.${adDomId}`, () => {
        PubSub.unsubscribe(this.stickyAdToken);
        const scrollTop = document.documentElement.scrollTop;
        // Cохраняем начальный сдвиг элемента относительно верха страницы
        this.defaultTopOffset = stickyEl.getBoundingClientRect().top + window.scrollY;
        // Если проскролено ниже чем начальное положение то сохранем это значение для будующих сравнений в runOnScroll и
        // прицепляем обработчкик к 'scroll'
        if (scrollTop > this.defaultTopOffset) {
          this.previousDocumentScroll = scrollTop;
          window.addEventListener('scroll', this.runOnScroll);
        } else {
        // Если нет, то просто делаем компонент static
          this.setState({ static_finish: true });
        }
      });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.static_offset && this.state.static_offset !== prevState.static_offset) {
      this.staticTopOffset = document.documentElement.scrollTop;
    }
  }

  componentWillUnmount() {
    if (this.props.listenStickyRemoveEvent) {
      PubSub.unsubscribe(this.stickyAdToken);
      window.removeEventListener('scroll', this.runOnScroll);
    }
  }

  runOnScroll = () => {
    const scrollTop = document.documentElement.scrollTop;
    // если проскролено меньше чем начальное положение компонента то делаем элемент static и удаляем обработчик
    if (scrollTop <= this.defaultTopOffset) {
      this.setState({ static_finish: true }, () => {
        window.removeEventListener('scroll', this.runOnScroll);
      });
    // если проскролено ниже чем начальное положение компонента то делаем элемент статическим и
    // добавляем ему margin-top(см render) чтобы он не прыгал вверх
    } else if (scrollTop > this.previousDocumentScroll && !this.state.static_offset) {
      this.setState({ static_offset: true });
    // если проскролено выше чем предыдущее сохраненное значение, то сохраняем новое значение для будующих сравнений и
    // если компонент static, то делаем его обратно sticky
    } else if (scrollTop < this.previousDocumentScroll) {
      this.previousDocumentScroll = scrollTop;
      if (this.state.static_offset) {
        this.setState({ static_offset: false });
      }
    }
  };

  render() {
    const {
      topOffset,
      theme: {
        controls: {
          shapka: {
            sticky: stickyShapka,
            height: shapkaHeight,
          },
        },
      },
      children,
      maxOffset,
    } = this.props;

    // Добавляем 10, чтобы баннер не прилипал к шапке
    const shapkaOffset = stickyShapka ? (shapkaHeight * sizeIndex + 10) : 0;
    const offset = topOffset || 0;
    const totalOffset = offset + shapkaOffset;

    return (
      <StickyContainer
        className={(this.state.static_finish || this.state.static_offset) ? styles.staticContainer : styles.container}
      >
        <div ref={this.stickyRef}>
          <Sticky topOffset={-offset}>
            {({ isSticky, style, distanceFromTop, distanceFromBottom, calculatedHeight }) => {
              const statusStyles = {
                [INITIAL]: {
                  top: isSticky ? style.top + totalOffset : undefined,
                },
                [UNSTICKY]: {
                  position: 'absolute',
                  top: maxOffset,
                  left: 0,
                },
                [STICKY_BOTTOM]: {
                  top: undefined,
                  bottom: 0,
                },
                [FINISH]: {
                  top: undefined,
                  position: 'absolute',
                  left: 0,
                  bottom: -offset,
                },
                [STATIC_OFFSET]: {
                  position: 'static',
                  marginTop: this.staticTopOffset > this.defaultTopOffset
                    ? this.staticTopOffset - this.defaultTopOffset + totalOffset
                    : 0,
                  width: 'auto',
                },
                [STATIC_FINISH]: {
                  position: 'static',
                },
              };

              // Высота окна
              const clientHeight = process.env.BROWSER_RUNTIME
                && window.document.documentElement.clientHeight;

              // Разница высоты окна и высоты элемента
              const deltaHeight = clientHeight - calculatedHeight;

              // Разница между макс. смещением и смещением стики элемента
              const deltaOffset = maxOffset + distanceFromTop;

              // Нужно ли контролировать максимальное смещение.
              // maxOffset должен быть задан и высота элемента должна быть
              // больше высоты окна.
              const needOffsetControl = maxOffset && deltaHeight < 0;

              let status = INITIAL;

              if (needOffsetControl) {
                // Проскролили до максимального значение, нужно зафиксировать
                // элемент.
                if (deltaOffset <= 0) {
                  status = UNSTICKY;
                }

                // Доскролили до конца элемента. Здесь стики элемент должен
                // прилипнуть к низу экрана.
                if (-deltaOffset + clientHeight >= calculatedHeight) {
                  status = STICKY_BOTTOM;
                }

                // Доскролили до конца. Нужно отлепить элемент снизу контейнера.
                if (
                  (distanceFromBottom <= 0 && status !== STICKY_BOTTOM) ||
                  (-deltaHeight <= -distanceFromBottom)
                ) {
                  status = FINISH;
                }
              } else {
                if (style.top < 0) {
                  status = FINISH;
                }
              }

              if (this.state.static_offset) {
                status = STATIC_OFFSET;
              }

              if (this.state.static_finish) {
                status = STATIC_FINISH;
              }

              let s;
              if (status === STATIC_FINISH) {
                s  = { ...statusStyles[status] };
              } else if (status === STATIC_OFFSET) {
                s = { ...style, ...statusStyles[status] };
              } else {
                s = {
                  ...style,
                  paddingBottom: offset,
                  ...statusStyles[status],
                };
              }

              return (
                <div
                  style={s}
                >
                  {children}
                </div>
              );
            }}
          </Sticky>
        </div>
      </StickyContainer>
    );
  }
}

StickyPortal.propTypes = {
  /**
   * Высота смещения относительно верхней границы вьюпорта.
   *
   * Может пригодится при совместном использовании с другими прикрепленными
   * элементами. Например компонент `Shapka`.
   */
  topOffset: PropTypes.number,

  /**
   * Максимальное смещение. Когда нужно отлеплять элемент.
   */
  maxOffset: PropTypes.number,

  /**
   * Из темы достаём высоту шапки для дополнительного отступа в случае залипающей шапки.
   */
  theme: PropTypes.object,

  /**
   * Контент, который будет рендерится в sticky-элементе
   */
  children: PropTypes.node,

  /**
   *  Флаг сообщающий компоненту что нужно слушать событие 'sticky.remove'
   */
  listenStickyRemoveEvent: PropTypes.bool,
};

StickyPortal.defaultProps = {
  topOffset: 0,
};

export default withTheme(StickyPortal);
